import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, Plus, Trash2, Save, Upload, Copy, Eye, EyeOff, Star, Calendar, Link2, Sparkles } from "lucide-react";

const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);
const blank = () => ({
  title: "", subtitle: "", date: "", start_time: "", end_time: "", venue_name: "", venue_address: "",
  host: "", cost: "", signup_url: "", signup_label: "Save your seat", description: "", who_for: "",
  flyer_url: "", extra_photos: [], source_url: "", status: "draft", featured: false,
});
const FIELDS = ["title", "subtitle", "date", "start_time", "end_time", "venue_name", "venue_address", "host", "cost", "signup_url", "signup_label", "description", "who_for", "flyer_url", "extra_photos", "source_url", "status", "featured"];
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function AdminTelegram() {
  const [token] = useState(() => getAdminToken());
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(blank());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Draft-from-link state
  const [draftUrl, setDraftUrl] = useState("");
  const [draftPaste, setDraftPaste] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [needsPaste, setNeedsPaste] = useState(false);
  const [draftNote, setDraftNote] = useState("");
  const [draftErr, setDraftErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/telegram/events`, auth);
      setRows(data.events || []);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load events.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const openEvent = (r) => { setSel(r.id); setForm({ ...blank(), ...r }); setConfirmDel(false); setMsg(""); setErr(""); };
  const openNew = () => { setSel("new"); setForm(blank()); setConfirmDel(false); setMsg(""); setErr(""); };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildBody = () => { const b = {}; FIELDS.forEach((k) => { b[k] = form[k]; }); return b; };

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      if (sel === "new") {
        const { data } = await axios.post(`${API}/telegram/events`, buildBody(), auth);
        setRows((r) => [...r, data]); setSel(data.id); setForm({ ...blank(), ...data });
      } else {
        const { data } = await axios.put(`${API}/telegram/events/${sel}`, buildBody(), auth);
        setRows((r) => r.map((x) => (x.id === sel ? data : x))); setForm({ ...blank(), ...data });
      }
      setMsg("Saved.");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const duplicate = async () => {
    if (sel === "new") { setErr("Save the event first, then duplicate."); return; }
    try {
      const { data } = await axios.post(`${API}/telegram/events/${sel}/duplicate`, {}, auth);
      setRows((r) => [...r, data]); openEvent(data); setMsg("Duplicated as a new draft.");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not duplicate.");
    }
  };

  const remove = async () => {
    if (sel === "new") { setSel(null); return; }
    try {
      await axios.delete(`${API}/telegram/events/${sel}`, auth);
      setRows((r) => r.filter((x) => x.id !== sel)); setSel(null); setForm(blank()); setConfirmDel(false);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not delete.");
    }
  };

  const applyDraft = (draft) => {
    // Open a fresh event (or keep the current one) and fill in the drafted fields.
    setSel((s) => s || "new");
    setForm((f) => {
      const base = (sel && sel !== "new") ? f : blank();
      const merged = { ...base };
      Object.keys(draft || {}).forEach((k) => {
        const v = draft[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") merged[k] = v;
      });
      merged.status = "draft";
      return merged;
    });
    setConfirmDel(false);
  };

  const draftFromLink = async (useText) => {
    setDrafting(true); setDraftErr(""); setDraftNote(""); setMsg(""); setErr("");
    try {
      const body = useText ? { url: draftUrl.trim(), pasted_text: draftPaste.trim() } : { url: draftUrl.trim() };
      const { data } = await axios.post(`${API}/telegram/draft-from-link`, body, auth);
      if (data.needs_paste) {
        setNeedsPaste(true);
        setDraftNote(data.message || "Couldn't read that post automatically — paste the caption below.");
        return;
      }
      applyDraft({ ...data.draft, source_url: data.source_url || draftUrl.trim() });
      setNeedsPaste(false); setDraftPaste("");
      setDraftNote("Drafted below — review and tweak, then Save.");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setDraftErr(e.response?.data?.detail || "Could not draft from that link.");
    } finally { setDrafting(false); }
  };

  const uploadFlyer = async (file) => {    if (!file) return;
    setUploading(true); setErr(""); setMsg("");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/telegram/upload`, fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      setField("flyer_url", data.url); setMsg("Flyer uploaded.");
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr(e.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const upcoming = rows.filter((r) => (r.date || "") >= todayStr());
  const past = rows.filter((r) => (r.date || "") < todayStr());
  const sortByDate = (a, b) => (a.date || "").localeCompare(b.date || "");
  upcoming.sort(sortByDate); past.sort((a, b) => sortByDate(b, a));

  const rowBtn = (r) => (
    <button key={r.id} onClick={() => openEvent(r)} data-testid={`telegram-row-${r.id}`}
      className={`block w-full rounded-xl p-3 text-left ring-1 transition ${sel === r.id ? "bg-gold/10 ring-gold/40" : "bg-ivory-50 ring-ivory-400/50 hover:ring-gold/30"} ${(r.date || "") < todayStr() ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 truncate font-display text-ink">
          {r.featured && <Star className="h-3.5 w-3.5 flex-none text-gold-deep" />} {r.title || "Untitled"}
        </span>
        {r.status !== "published" && <EyeOff className="h-3.5 w-3.5 flex-none text-ink-muted" />}
      </div>
      <p className="mt-0.5 truncate text-xs text-ink-muted">{r.date || "no date"}{r.start_time ? ` · ${r.start_time}` : ""} · {r.status}</p>
    </button>
  );

  const Label = ({ children }) => <span className="text-sm text-ink-soft">{children}</span>;
  const inputCls = "mt-1 w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink";

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-telegram">
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-ink">The Telegram</h1>
            <p className="mt-1 text-sm text-ink-soft">Events, workshops and talks shown at <code className="rounded bg-ink/5 px-1">/telegram</code>.</p>
          </div>
          <button onClick={openNew} data-testid="telegram-new-event" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink">
            <Plus className="h-4 w-4" /> New event
          </button>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 sm:grid-cols-[320px_1fr] sm:px-8">
        <div className="space-y-2">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
          {!loading && rows.length === 0 && (
            <div className="rounded-xl bg-ivory-50 p-6 text-center ring-1 ring-ivory-400/50"><p className="text-sm text-ink-muted">No events yet.</p></div>
          )}
          {upcoming.length > 0 && <p className="px-1 pt-1 text-[11px] uppercase tracking-wide text-ink-muted">Upcoming</p>}
          {upcoming.map(rowBtn)}
          {past.length > 0 && <p className="px-1 pt-3 text-[11px] uppercase tracking-wide text-ink-muted">Past</p>}
          {past.map(rowBtn)}
        </div>

        <div>
          <div className="mb-5 rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/50" data-testid="telegram-draft-panel">
            <p className="flex items-center gap-2 font-display text-ink">
              <Sparkles className="h-4 w-4 text-gold-deep" /> Draft from a Facebook / Instagram link
            </p>
            <p className="mt-1 text-xs text-ink-soft">Paste an event link and I'll draft the details for you to review.</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                <input value={draftUrl} onChange={(e) => setDraftUrl(e.target.value)} data-testid="telegram-draft-url"
                  placeholder="https://facebook.com/events/… or instagram.com/p/…"
                  className="w-full rounded-lg border border-ink/15 bg-white py-2 pl-9 pr-3 text-ink" />
              </div>
              <button onClick={() => draftFromLink(false)} disabled={drafting || !draftUrl.trim()} data-testid="telegram-draft-btn"
                className="debossed-btn inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 font-display text-ink disabled:opacity-50">
                {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft event
              </button>
            </div>
            {needsPaste && (
              <div className="mt-3" data-testid="telegram-draft-paste-block">
                <textarea value={draftPaste} onChange={(e) => setDraftPaste(e.target.value)} rows={5} data-testid="telegram-draft-paste"
                  placeholder="Paste the event caption / details here…"
                  className="w-full rounded-lg border border-ink/15 bg-white px-3 py-2 text-ink" />
                <button onClick={() => draftFromLink(true)} disabled={drafting || !draftPaste.trim()} data-testid="telegram-draft-from-text"
                  className="mt-2 inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 font-display text-sm text-ink-soft disabled:opacity-50">
                  {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft from this text
                </button>
              </div>
            )}
            {draftNote && <p className="mt-3 text-sm text-gold-deep" data-testid="telegram-draft-note">{draftNote}</p>}
            {draftErr && <p className="mt-3 text-sm text-terracotta" data-testid="telegram-draft-error">{draftErr}</p>}
          </div>
          {!sel ? (
            <p className="text-sm text-ink-muted">Select an event to edit, or create a new one.</p>
          ) : (
            <div className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/50 sm:p-7" data-testid="telegram-editor">
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setField("status", form.status === "published" ? "draft" : "published")} data-testid="telegram-publish-toggle"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${form.status === "published" ? "bg-sage/25 text-ink" : "border border-ink/20 text-ink-soft"}`}>
                  {form.status === "published" ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {form.status === "published" ? "Published" : "Draft"}
                </button>
                <button onClick={() => setField("featured", !form.featured)} data-testid="telegram-featured-toggle"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${form.featured ? "bg-gold/20 text-ink" : "border border-ink/20 text-ink-soft"}`}>
                  <Star className="h-3.5 w-3.5" /> {form.featured ? "Featured" : "Feature"}
                </button>
              </div>

              <div className="mt-5 grid gap-4">
                <label><Label>Title *</Label><input value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="telegram-field-title" className={`${inputCls} font-display`} /></label>
                <label><Label>Subtitle / tagline</Label><input value={form.subtitle} onChange={(e) => setField("subtitle", e.target.value)} data-testid="telegram-field-subtitle" className={inputCls} /></label>
                <div className="grid grid-cols-3 gap-4">
                  <label><Label>Date *</Label><input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="telegram-field-date" className={inputCls} /></label>
                  <label><Label>Start time</Label><input value={form.start_time} onChange={(e) => setField("start_time", e.target.value)} placeholder="11:00 a.m." data-testid="telegram-field-start" className={inputCls} /></label>
                  <label><Label>End time</Label><input value={form.end_time} onChange={(e) => setField("end_time", e.target.value)} placeholder="12:30 p.m." data-testid="telegram-field-end" className={inputCls} /></label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label><Label>Venue name</Label><input value={form.venue_name} onChange={(e) => setField("venue_name", e.target.value)} data-testid="telegram-field-venue" className={inputCls} /></label>
                  <label><Label>Street address</Label><input value={form.venue_address} onChange={(e) => setField("venue_address", e.target.value)} data-testid="telegram-field-address" className={inputCls} /></label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label><Label>Host / partner</Label><input value={form.host} onChange={(e) => setField("host", e.target.value)} data-testid="telegram-field-host" className={inputCls} /></label>
                  <label><Label>Cost</Label><input value={form.cost} onChange={(e) => setField("cost", e.target.value)} placeholder="Free" data-testid="telegram-field-cost" className={inputCls} /></label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label><Label>Sign-up link (URL)</Label><input value={form.signup_url} onChange={(e) => setField("signup_url", e.target.value)} data-testid="telegram-field-signup" className={inputCls} /></label>
                  <label><Label>Button label</Label><input value={form.signup_label} onChange={(e) => setField("signup_label", e.target.value)} data-testid="telegram-field-signuplabel" className={inputCls} /></label>
                </div>
                <label><Label>Who it's for</Label><input value={form.who_for} onChange={(e) => setField("who_for", e.target.value)} data-testid="telegram-field-whofor" className={inputCls} /></label>
                <label><Label>Description</Label><textarea value={form.description} onChange={(e) => setField("description", e.target.value)} rows={6} data-testid="telegram-field-description" className={inputCls} /></label>
                <label><Label>Source link (optional)</Label><input value={form.source_url} onChange={(e) => setField("source_url", e.target.value)} placeholder="Facebook / Instagram post" data-testid="telegram-field-source" className={inputCls} /></label>
              </div>

              <div className="mt-5 rounded-xl bg-ivory-100 p-4 ring-1 ring-ivory-400/50">
                <p className="flex items-center gap-2 text-sm text-ink"><Upload className="h-4 w-4 text-gold-deep" /> Flyer image <span className="text-xs text-terracotta">(required to publish)</span></p>
                {form.flyer_url && <img src={imgSrc(form.flyer_url)} alt="Flyer preview" data-testid="telegram-flyer-preview" className="mt-3 max-h-64 rounded-lg ring-1 ring-ivory-400/60" />}
                <input ref={fileRef} type="file" accept="image/*" data-testid="telegram-flyer-input" onChange={(e) => uploadFlyer(e.target.files?.[0])}
                  className="mt-3 block w-full text-sm text-ink-soft file:mr-3 file:rounded-full file:border-0 file:bg-ink file:px-4 file:py-2 file:text-ivory-50" />
                <input value={form.flyer_url || ""} onChange={(e) => setField("flyer_url", e.target.value)} placeholder="…or paste an image URL" data-testid="telegram-field-flyerurl" className={`${inputCls} mt-2`} />
                {uploading && <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-soft"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</p>}
              </div>

              {err && <p className="mt-4 text-sm text-terracotta" data-testid="telegram-editor-error">{err}</p>}
              {msg && <p className="mt-4 text-sm text-gold-deep" data-testid="telegram-editor-msg">{msg}</p>}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button onClick={save} disabled={saving} data-testid="telegram-save" className="debossed-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </button>
                {sel !== "new" && (
                  <button onClick={duplicate} data-testid="telegram-duplicate" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2.5 font-display text-sm text-ink-soft">
                    <Copy className="h-4 w-4" /> Duplicate
                  </button>
                )}
                {!confirmDel ? (
                  <button onClick={() => setConfirmDel(true)} data-testid="telegram-delete" className="inline-flex items-center gap-2 rounded-full border border-terracotta/40 px-4 py-2.5 font-display text-sm text-terracotta">
                    <Trash2 className="h-4 w-4" /> {sel === "new" ? "Cancel" : "Delete"}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 px-3 py-1.5 text-sm text-terracotta" data-testid="telegram-delete-confirm">
                    Delete this event?
                    <button onClick={remove} data-testid="telegram-delete-yes" className="rounded-full bg-terracotta px-3 py-1 text-ivory-50">Yes</button>
                    <button onClick={() => setConfirmDel(false)} className="rounded-full border border-ink/20 px-3 py-1 text-ink-soft">No</button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
